import { Point } from "geojson";

import { DEVICE_BASE_COLUMNS, DeviceBase } from "./devices";

import { ColumnsOf } from "../data";
import { Maybe, Modify } from "../utility";

export const anchorType = {
  Luna: "anchor.luna",
  Wifi: "anchor.wifi",
  Gateway: "anchor.gateway",
  Tracker: "anchor.tracker"
} as const;

export const allAnchorTypes = Object.values(anchorType);

type AnchorTypeObject = typeof anchorType;

type AnchorTypeKeys = keyof AnchorTypeObject;

export type AnchorType = AnchorTypeObject[AnchorTypeKeys];

export interface Anchor extends DeviceBase<AnchorType> {
  bssid?: Maybe<string>;
  bssids?: Maybe<string[]>;
  ssid: Maybe<string>;
  anchor_gwid?: Maybe<string>;
  fixed_location?: Maybe<Point>;
  fixed_radius?: Maybe<number>;
}

export type AnchorLunaInsert = Modify<
  Pick<Anchor, "name" | "tid" | "fixed_radius"> &
    Required<Pick<Anchor, "fixed_location">> &
    Partial<Pick<Anchor, "tags">>,
  { type: AnchorTypeObject["Luna"] }
>;

export type AnchorWifiInsert = Modify<
  Pick<Anchor, "name" | "bssids" | "fixed_radius"> &
    Required<Pick<Anchor, "fixed_location">> &
    Partial<Pick<Anchor, "tags">>,
  { type: AnchorTypeObject["Wifi"] }
>;

export type AnchorGatewayInsert = Modify<
  Pick<Anchor, "name" | "gwid" | "fixed_radius"> &
    Required<Pick<Anchor, "fixed_location">> &
    Partial<Pick<Anchor, "tags">>,
  { type: AnchorTypeObject["Gateway"] }
>;

export type AnchorInsert = AnchorWifiInsert | AnchorLunaInsert | AnchorGatewayInsert;

export type AnchorLunaUpdate = Modify<
  Partial<Pick<Anchor, "name" | "tags" | "tid" | "fixed_location" | "fixed_radius">>,
  { type?: AnchorTypeObject["Luna"] }
>;

export type AnchorWifiUpdate = Modify<
  Partial<Pick<Anchor, "name" | "tags" | "bssids" | "fixed_location" | "fixed_radius">>,
  { type?: AnchorTypeObject["Wifi"] }
>;

export type AnchorGatewayUpdate = Modify<
  Partial<Pick<Anchor, "name" | "tags" | "gwid" | "fixed_location" | "fixed_radius">>,
  { type?: AnchorTypeObject["Gateway"] }
>;

export type AnchorUpdate = AnchorWifiUpdate | AnchorLunaUpdate | AnchorGatewayUpdate;

export type AnchorCommonCsv = {
  "anchor name": string;
  workspace: string;
  type: AnchorType;
  tags?: string[];
  latitude: number;
  longitude: number;
};

export type AnchorGatewayCsv = {
  type: AnchorTypeObject["Gateway"];
  "gateway id": string;
} & AnchorCommonCsv;

export type AnchorWifiCsv = {
  type: AnchorTypeObject["Wifi"];
  bssids: string;
  ssid: string;
} & AnchorCommonCsv;

export type AnchorCsv = AnchorGatewayCsv | AnchorWifiCsv;

export type AnchorPreview = Pick<Anchor, "type"> & { radius?: Maybe<number>; location: Point };
export const ANCHOR_COLUMNS: ColumnsOf<Anchor> = [...DEVICE_BASE_COLUMNS] as const;
